import React, { useContext, useEffect, useState } from 'react';
import proptypes from 'prop-types';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import {
  arrayOf as arrayOfType,
  bool as boolType,
  number as numberType,
  shape as shapeType,
  string as stringType,
  params,
  useDataModel
} from '@thd-nucleus/data-sources';
import { useImpression } from '@thd-olt-component-react/impression';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { MiniSwatch } from './mini-swatch';
import { publish } from '../../publisher';
import { MAX_NUMBER_OF_MINI_SWATCHES } from '../../statics';
import { isWindowExists, isLifeCycleEventExists } from '../../utils';

const {
  bool, func, number, oneOf, shape, string
} = proptypes;

// container query classes from stencil, sized to hide until swatch container has
// space for 3, 4 and 5 default size '6' mini swatches and padding+margins (roughly 32px)
const MINI_SWATCH_WRAPPER_CLASSES = {
  0: 'sui-mr-1 sui-pr-1px',
  1: 'sui-mr-1 sui-pr-1px',
  2: 'sui-mr-1 sui-pr-1px sui-hidden @[100px]/ssms:sui-block',
  3: 'sui-mr-1 sui-pr-1px sui-hidden @[132px]/ssms:sui-block',
  4: 'sui-mr-1 sui-pr-1px sui-hidden @[164px]/ssms:sui-block'
};

const shouldShowMoreOptionsLink = (
  numberOfOptions = 0,
  availableSwatches = [],
  numberOfMiniSwatchesToDisplay = 0
) => {
  const displayedCount = Math.min(availableSwatches?.length || 0, numberOfMiniSwatchesToDisplay);
  return numberOfOptions > displayedCount && numberOfOptions > 1;
};

const getMoreOptionsLinkText = (displayedCount, otherOptionsCount) => {
  if (displayedCount === 0) {
    return '+more options';
  }
  return otherOptionsCount > 0 ? `+${otherOptionsCount} more` : '+more';
};

export const SuperSkuMiniSwatches = ({
  itemId,
  onChange,
  onHover,
  numberOfMiniSwatches,
  center,
  componentImpressionData,
  isImpressionsRequired,
  showLabelWithSwatches,
  removeStaticHeight,
  hideComponentWhenEmpty,
  miniSwatchSize
}) => {
  const {
    channel = 'desktop'
  } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const { greater } = useBreakpoint('sm');
  const enableHover = Boolean(greater);
  const swatchNavigatestoPip = useConfigService('fs-prop:isSwatchNavigatestoPIPEnabled') || false;

  const [state, setState] = useState({
    swatches: [],
    parentId: null,
    shouldSkip: false,
    isSwatchClicked: false,
    selectedItemLabel: null
  });

  const { data } = useDataModel('product', {
    variables: { itemId },
    fetchPolicy: 'cache-first'
  });

  const { additionalData = {} } = useImpression({
    data: {
      id: itemId,
      component: 'SuperSkuMiniSwatches',
      name: 'SuperSkuMiniSwatches',
      position: 0
    }
  });

  // impressions data
  const { parent } = additionalData;

  // product data
  const { parentId, canonicalUrl } = data?.product?.identifiers || {};
  const {
    swatches: swatchesData = [],
    totalNumberOfOptions: totalNumberOfOptionsAvailable = 0
  } = data?.product?.info || {};

  // swatch state management hooks
  useEffect(() => {
    const { swatches = [], totalNumberOfOptions = 0 } = data?.product?.info || {};

    if (swatches?.length && !state.shouldSkip) {
      const firstSwatchLabel = swatches?.length > 0 ? swatches[0]?.label : null;
      setState((prevState) => ({
        ...prevState,
        swatches,
        shouldSkip: true,
        totalNumberOfOptions,
        parentId: parentId || null,
        selectedItemLabel: firstSwatchLabel
      }));
    } else if (totalNumberOfOptions > 0) {
      setState((prevState) => ({
        ...prevState,
        totalNumberOfOptions
      }));
    }
  }, [data?.product?.info, parentId]);

  useEffect(() => {
    if (state?.isSwatchClicked) {
      publish('click-swatch', {});

      const selectedItem = state.swatches.filter((swatch) => swatch.isSelected);
      const selectedItemId = selectedItem?.length ? selectedItem[0].itemId : '';

      if (selectedItemId) {
        onChange({ itemId: selectedItemId });
      }
      setState((prevState) => ({
        ...prevState,
        isSwatchClicked: false
      }));
    }
  }, [state.isSwatchClicked]);

  const handleAttributeValueClick = (clickedItemId) => {
    const newSwatches = state.swatches.map((swatch) => {
      return {
        ...swatch,
        isSelected: swatch?.itemId === clickedItemId
      };
    });
    const selectedItems = newSwatches.filter((swatch) => swatch.isSelected);
    const selectedItem = selectedItems.length > 0 ? selectedItems[0] : null;

    if (swatchNavigatestoPip && selectedItem?.url) {
      if (isWindowExists()) {
        window.location.assign(selectedItem?.url);
      }
    } else if (selectedItem) {
      setState((prevState) => ({
        ...prevState,
        swatches: newSwatches,
        isSwatchClicked: true,
        selectedItemLabel: selectedItem?.label || ''
      }));
    }
  };

  const handleAttributeValueHover = (hoveredItemId) => {
    const hoveredSwatch = state.swatches.find((swatch) => swatch.itemId === hoveredItemId);
    if (hoveredSwatch) {
      onHover({
        itemId: hoveredItemId,
        isHovered: true,
        selectedItemLabel: hoveredSwatch?.label || ''
      });
      setState((prevState) => ({
        ...prevState,
        itemId: hoveredItemId,
        isHovered: true,
        selectedItemLabel: hoveredSwatch?.label || ''
      }));
    }
  };

  const handleResetHover = () => {
    const selectedSwatch = state.swatches.find((swatch) => swatch.isSelected);
    onHover({
      itemId: selectedSwatch?.itemId || itemId,
      isHovered: false,
      selectedItemLabel: selectedSwatch?.label || ''
    });
    setState((prevState) => ({
      ...prevState,
      itemId: selectedSwatch?.itemId || itemId,
      isHovered: false,
      selectedItemLabel: selectedSwatch?.label || ''
    }));
  };

  const handleOptionsLinkClick = (_event, action = null) => {
    const productMiniSwatchEventData = {
      podAction: action || 'more options link',
      podAnchorSku: itemId,
      target: '',
      parent,
    };

    if (isLifeCycleEventExists()) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('product-pod-v7.click', productMiniSwatchEventData);
    }
  };

  // Always 5 maximum unless prop is passed. Using container queries to control hiding as needed.
  const numberOfMiniSwatchesToDisplay = numberOfMiniSwatches || MAX_NUMBER_OF_MINI_SWATCHES;

  const { swatches = [], totalNumberOfOptions = 0 } = state;
  const numberOfOptions = totalNumberOfOptionsAvailable || totalNumberOfOptions;
  // Both swatches and swatchesData default to [], but prefer state swatches if available
  const availableSwatches = swatches.length > 0 ? swatches : swatchesData;
  // Since both default to [], this should always be an array, but we add fallback just in case
  const toDisplay = (availableSwatches || []).slice(0, numberOfMiniSwatchesToDisplay);

  const displaySwatches = toDisplay?.length > 1;
  const displaySwatchesSpaceInMobile = toDisplay?.length >= 2;
  const displaySwatchesSpace = isMobile ? displaySwatchesSpaceInMobile : true;

  const showMoreOptionsLink = shouldShowMoreOptionsLink(numberOfOptions,
    availableSwatches, numberOfMiniSwatchesToDisplay);

  // Number of other options not shown in available swatches being displayed
  const otherOptionsCount = (numberOfOptions - toDisplay?.length) || 0;

  const safeMiniSwatchSize = ['4', '5', '6', '7', '8', '9'].includes(miniSwatchSize) ? miniSwatchSize : '6';

  const hoveredSwatchLabelClasses = [
    `${removeStaticHeight ? '' : 'sui-h-6'}`,
    'sui-pt-1 sui-font-regular sui-text-xs sui-text-subtle'
  ].join(' ');

  const sskuSwatchContainerClasses = [
    'sui-inline-flex sui-flex-wrap sui-items-center sui-pl-1 sui-py-1',
    `${center ? 'sui-justify-center' : 'sui-justify-start'}`,
    `${removeStaticHeight ? '' : 'sui-min-h-11'}`,
    'sui-@container/ssms sui-w-full'
  ].join(' ');

  const optionsLinkClasses = [
    `${removeStaticHeight ? '' : 'sui-h-11'}`,
    'sui-content-center sui-font-regular sui-text-sm sui-text-primary sui-underline'
  ].join(' ');

  if (hideComponentWhenEmpty && (!displaySwatchesSpace && !showMoreOptionsLink)) {
    return <></>;
  }

  return (
    <div data-testid="ssku-min-swatch" data-component="SuperSkuMiniSwatches">
      {displaySwatches && showLabelWithSwatches && (
        <div className={hoveredSwatchLabelClasses}>
          {state?.selectedItemLabel && (
            <div>{state.selectedItemLabel}</div>
          )}
        </div>
      )}
      <div className={sskuSwatchContainerClasses}>
        {displaySwatches && toDisplay?.map((swatch, index) => {
          const {
            isSelected,
            itemId: swatchItemId,
            label,
            swatchImgUrl
          } = swatch || {};

          if (!swatchImgUrl) return null;

          return (
            <MiniSwatch
              wrapperClasses={MINI_SWATCH_WRAPPER_CLASSES[index] || 'sui-mr-1 sui-pr-1px'}
              key={`ssku-${swatchImgUrl}`}
              enableHover={enableHover}
              isSelected={isSelected}
              onClick={() => handleAttributeValueClick(swatchItemId)}
              onMouseEnter={(() => handleAttributeValueHover(swatchItemId))}
              onMouseLeave={handleResetHover}
              swatchGuid={swatchImgUrl}
              value={label}
              index={index}
              swatchItemId={swatchItemId}
              componentImpressionData={componentImpressionData}
              isImpressionsRequired={isImpressionsRequired}
              ariaLabel={`${label || 'Swatch'} color or pattern variation`}
              swatchName={label}
              swatchSize={safeMiniSwatchSize}
            />
          );
        })}
        {showMoreOptionsLink && canonicalUrl && (
          <a
            className="sui-flex sui-flex-col"
            aria-label="product show more options link"
            href={canonicalUrl}
            onClick={handleOptionsLinkClick}
          >
            <div className={optionsLinkClasses}>
              {getMoreOptionsLinkText(toDisplay.length, otherOptionsCount)}
            </div>
          </a>
        )}
      </div>
    </div>
  );
};

SuperSkuMiniSwatches.propTypes = {
  itemId: string.isRequired,
  onChange: func.isRequired,
  onHover: func,
  numberOfMiniSwatches: number,
  center: bool,
  componentImpressionData: shape({
    name: string,
    component: string,
    position: number,
    type: string,
    anchorSku: string
  }),
  isImpressionsRequired: bool,
  showLabelWithSwatches: bool,
  /* Remove static height classes, allow dynamic component height */
  removeStaticHeight: bool,
  /* Return react fragment element when no swatches or links to show */
  hideComponentWhenEmpty: bool,
  /** Default '6'; See Stencil Design System 'Spacing Tokens' for available pixel values */
  miniSwatchSize: oneOf(['4', '5', '6', '7', '8', '9'])
};

SuperSkuMiniSwatches.defaultProps = {
  onHover: () => { },
  numberOfMiniSwatches: null,
  center: false,
  componentImpressionData: {
    name: '',
    component: '',
    position: 1,
    type: '',
    anchorSku: ''
  },
  isImpressionsRequired: false,
  /* Reserves space above mini swatches for swatch label text. */
  showLabelWithSwatches: false,
  /* Remove static height classes, allow dynamic component height */
  removeStaticHeight: false,
  /* Return react fragment element when no swatches or links to show */
  hideComponentWhenEmpty: false,
  miniSwatchSize: '6'
};

SuperSkuMiniSwatches.dataModel = {
  product: params({
    itemId: stringType().isRequired(),
    dataSource: stringType()
  }).shape({
    itemId: stringType(),
    dataSources: stringType(),
    identifiers: shapeType({
      canonicalUrl: stringType(),
      parentId: stringType()
    }),
    info: shapeType({
      swatches: arrayOfType(shapeType({
        isSelected: boolType(),
        itemId: stringType(),
        label: stringType(),
        swatchImgUrl: stringType(),
        url: stringType(),
        value: stringType()
      })),
      totalNumberOfOptions: numberType()
    })
  })
};
